import React, { useState, useEffect, useRef } from "react";
import { entities, getDisplayMessage } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, Send, FileText, Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import ItemsProposta from "../components/propostas/ItemsProposta";
import { buildPropostaItensFromEditalItens } from "@/utils/editalToPropostaItens";
import SelecionarPregaoModal from "../components/propostas/SelecionarPregaoModal";
import { Search } from "lucide-react";

export default function CriarPropostaPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const urlParams = new URLSearchParams(window.location.search);
  const propostaId = urlParams.get('id');

  const [proposta, setProposta] = useState({
    edital_id: "",
    numero_proposta: "",
    data_proposta: new Date().toISOString().split('T')[0],
    status: "rascunho",
    ao_instituicao: "",
    agente_contratacao: "",
    pregao_numero: "",
    processo_numero: "",
    sistema: "",
    identificacao: "",
    objeto: "",
    itens: [],
    valor_total_proposta: 0,
    prazo_validade: "",
    prazo_entrega: "",
    prazo_vigencia: "",
    prazo_pagamento: "",
    prazos_extras: [],
    observacoes: "",
    texto_valor_total: null,
  });

  const {
    data: editais = [],
    isPending: isPendingEditais,
    isFetching: isFetchingEditais,
    error: errorEditais,
    refetch: refetchEditais,
  } = useQuery({
    queryKey: ['editais-criar-proposta', { modo: 'proposta', limit: 500, v: 3 }],
    /** Sem filtro de status no servidor — o modal lista todos os pregões da empresa e prioriza extração concluída. */
    queryFn: () => entities.Edital.list('-updatedAt', 500),
    staleTime: 30_000,
  });

  const { data: config } = useQuery({
    queryKey: ['config-proposta'],
    queryFn: async () => {
      const configs = await entities.ConfiguracaoEmpresa.list();
      return configs[0] || null;
    }
  });

  const [isLoading, setIsLoading] = useState(!!propostaId);
  const [modalPregaoAberto, setModalPregaoAberto] = useState(false);
  /** Proposta avulsa: sem vínculo com edital/pregão no sistema (edital_id nulo). */
  const [semPregao, setSemPregao] = useState(false);
  /** Só true após importar planilha em ItemsProposta; usado para avisar ao trocar pregão. */
  const [itensImportadosPlanilha, setItensImportadosPlanilha] = useState(false);
  /** Evita aplicar GET de pregão antigo se o utilizador selecionar outro antes da resposta (corrida async). */
  const editalSelecionadoRef = useRef(null);

  useEffect(() => {
    if (modalPregaoAberto) {
      void refetchEditais();
    }
  }, [modalPregaoAberto, refetchEditais]);

  useEffect(() => {
    if (propostaId) {
      carregarProposta();
    }
  }, [propostaId]);

  const carregarProposta = async () => {
    setIsLoading(true);
    try {
      const propostas = await entities.Proposta.list();
      const propostaEncontrada = propostas.find(p => p.id === propostaId);
      if (propostaEncontrada) {
        // Normaliza para snake_case e formata datas (API retorna camelCase)
        const p = propostaEncontrada;
        const dataPropostaRaw = p.dataProposta ?? p.data_proposta;
        const dataPropostaStr = dataPropostaRaw
          ? (typeof dataPropostaRaw === 'string'
            ? dataPropostaRaw.includes('T') ? dataPropostaRaw.split('T')[0] : dataPropostaRaw
            : new Date(dataPropostaRaw).toISOString().split('T')[0])
          : new Date().toISOString().split('T')[0];

        const editalCarregado = p.editalId ?? p.edital_id ?? "";
        setSemPregao(!editalCarregado);
        setProposta({
          edital_id: editalCarregado,
          numero_proposta: p.numeroProposta ?? p.numero_proposta ?? "",
          data_proposta: dataPropostaStr,
          status: p.status ?? "rascunho",
          ao_instituicao: p.aoInstituicao ?? p.ao_instituicao ?? "",
          agente_contratacao: p.agenteContratacao ?? p.agente_contratacao ?? "",
          pregao_numero: p.pregaoNumero ?? p.pregao_numero ?? "",
          processo_numero: p.processoNumero ?? p.processo_numero ?? "",
          sistema: p.sistema ?? "",
          identificacao: p.identificacao ?? "",
          objeto: p.objeto ?? "",
          itens: p.itens ?? [],
          colunas_itens: p.colunasItens ?? p.colunas_itens ?? null,
          campos_extras: p.camposExtras ?? p.campos_extras ?? [],
          valor_total_proposta: p.valorTotalProposta ?? p.valor_total_proposta ?? 0,
          prazo_validade: p.prazoValidade ?? p.prazo_validade ?? "",
          prazo_entrega: p.prazoEntrega ?? p.prazo_entrega ?? "",
          prazo_vigencia: p.prazoVigencia ?? p.prazo_vigencia ?? "",
          prazo_pagamento: p.prazoPagamento ?? p.prazo_pagamento ?? "",
          prazos_extras: (() => {
            const raw = p.prazosExtras ?? p.prazos_extras;
            if (!Array.isArray(raw)) return [];
            return raw.map((x) => ({
              id:
                x && typeof x.id === "string" && x.id
                  ? x.id
                  : crypto.randomUUID(),
              titulo: x?.titulo != null ? String(x.titulo) : "",
              conteudo: x?.conteudo != null ? String(x.conteudo) : "",
            }));
          })(),
          observacoes: p.observacoes ?? "",
          local_data_proposta: p.localDataProposta ?? p.local_data_proposta ?? null,
          texto_valor_total: p.textoValorTotal ?? p.texto_valor_total ?? null,
        });
      }
    } catch (error) {
      console.error("Erro ao carregar proposta:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (propostaId) {
        return entities.Proposta.update(propostaId, data);
      } else {
        return entities.Proposta.create(data);
      }
    },
    onSuccess: () => {
      toast.success("Proposta salva com sucesso!");
      queryClient.invalidateQueries({ queryKey: ['propostas'] });
      navigate(createPageUrl("Propostas"));
    },
    onError: (error) => {
      toast.error(getDisplayMessage(error, "Erro ao salvar proposta. Tente novamente."));
    }
  });

  // Função para salvar imediatamente (usada na importação de planilha)
  const handleSaveImmediate = async (propostaAtualizada) => {
    console.log("=== handleSaveImmediate INICIADO ===");
    console.log("propostaId:", propostaId);
    console.log("Itens recebidos:", propostaAtualizada.itens?.length);
    console.log("Colunas recebidas:", propostaAtualizada.colunas_itens?.length);
    console.log("Primeiro item:", propostaAtualizada.itens?.[0]);
    
    if (!semPregao && !propostaAtualizada.edital_id && !proposta.edital_id) {
      console.warn("Sem edital_id, salvamento adiado");
      setProposta(propostaAtualizada);
      return;
    }

    const editalIdSalvar = semPregao
      ? null
      : (propostaAtualizada.edital_id || proposta.edital_id || null);

    const dadosParaSalvar = {
      edital_id: editalIdSalvar,
      numero_proposta: propostaAtualizada.numero_proposta || "",
      data_proposta: propostaAtualizada.data_proposta,
      local_data_proposta: propostaAtualizada.local_data_proposta || null,
      texto_valor_total: propostaAtualizada.texto_valor_total || null,
      status: propostaAtualizada.status || "rascunho",
      ao_instituicao: propostaAtualizada.ao_instituicao || "",
      agente_contratacao: propostaAtualizada.agente_contratacao || "",
      pregao_numero: propostaAtualizada.pregao_numero || "",
      processo_numero: propostaAtualizada.processo_numero || "",
      sistema: propostaAtualizada.sistema || "",
      identificacao: propostaAtualizada.identificacao || "",
      objeto: propostaAtualizada.objeto || "",
      itens: propostaAtualizada.itens || [],
      colunas_itens: propostaAtualizada.colunas_itens || null,
      campos_extras: propostaAtualizada.campos_extras || [],
      valor_total_proposta: propostaAtualizada.valor_total_proposta || 0,
      prazo_validade: propostaAtualizada.prazo_validade || "",
      prazo_entrega: propostaAtualizada.prazo_entrega || "",
      prazo_vigencia: propostaAtualizada.prazo_vigencia || "",
      prazo_pagamento: propostaAtualizada.prazo_pagamento || "",
      prazos_extras: propostaAtualizada.prazos_extras || [],
      observacoes: propostaAtualizada.observacoes || ""
    };
    
    console.log("Dados a salvar:", {
      itens: dadosParaSalvar.itens.length,
      colunas: dadosParaSalvar.colunas_itens?.length,
      primeiroItem: dadosParaSalvar.itens[0]
    });
    
    try {
      let resultado;
      if (propostaId) {
        console.log("Atualizando proposta existente:", propostaId);
        resultado = await entities.Proposta.update(propostaId, dadosParaSalvar);
        console.log("Resultado da atualização:", resultado);
        setProposta(prev => ({ ...prev, ...dadosParaSalvar }));
      } else {
        console.log("Criando nova proposta");
        resultado = await entities.Proposta.create(dadosParaSalvar);
        console.log("Nova proposta criada:", resultado.id);
        setProposta({ ...dadosParaSalvar, id: resultado.id });
        // Redireciona para edição
        navigate(createPageUrl("CriarProposta") + `?id=${resultado.id}`, { replace: true });
      }
      queryClient.invalidateQueries({ queryKey: ['propostas'] });
      console.log("=== handleSaveImmediate CONCLUÍDO COM SUCESSO ===");
    } catch (error) {
      console.error("=== ERRO NO handleSaveImmediate ===");
      console.error("Erro completo:", error);
      throw error;
    }
  };

  const handleEditalChange = async (editalId) => {
    setSemPregao(false);
    editalSelecionadoRef.current = editalId;
    let edital = (Array.isArray(editais) ? editais : []).find((e) => e.id === editalId);
    try {
      const completo = await entities.Edital.get(editalId);
      if (completo && typeof completo === "object") {
        edital = completo;
      }
    } catch (e) {
      console.warn("Não foi possível recarregar o edital completo; usando dados da lista.", e);
    }
    if (editalSelecionadoRef.current !== editalId) return;
    if (!edital) return;

    setItensImportadosPlanilha((hadImport) => {
      if (hadImport) {
        toast.info(
          "Itens importados da planilha foram substituídos pelos itens do pregão selecionado."
        );
      }
      return false;
    });

    const rawItens = edital.itens;
    const colsEdital = edital.colunas_itens ?? edital.colunasItens ?? null;

    let itens = [];
    let colunas_itens = null;
    let usouIa = false;
    if (Array.isArray(rawItens) && rawItens.length > 0) {
      try {
        const ia = await entities.Edital.itensParaPropostaIA(editalId);
        if (
          ia &&
          Array.isArray(ia.itens) &&
          ia.itens.length > 0 &&
          Array.isArray(ia.colunas_itens) &&
          ia.colunas_itens.length > 0
        ) {
          itens = ia.itens;
          colunas_itens = ia.colunas_itens;
          usouIa = true;
        }
      } catch (e) {
        if (e?.status === 503) {
          toast.info("IA não configurada ou indisponível — itens montados automaticamente.");
        } else if (e?.status === 500) {
          toast.warning("IA não conseguiu mapear os itens — usando montagem automática.");
          console.warn("Itens para proposta (IA):", e);
        } else {
          console.warn("Itens para proposta (IA):", e);
        }
      }
    }
    if (!usouIa) {
      const built = buildPropostaItensFromEditalItens(rawItens, {
        colunasItens: colsEdital,
        tituloGrupoUnico:
          edital.numero_pregao || edital.numeroPregao
            ? `Itens — Pregão ${edital.numero_pregao ?? edital.numeroPregao}`
            : "Itens do pregão",
      });
      itens = built.itens;
      colunas_itens = built.colunas_itens;
    }

    if (usouIa) {
      toast.success("Itens replicados do edital com apoio de IA.");
    }

    setProposta((prev) => ({
      ...prev,
      edital_id: editalId,
      ao_instituicao: edital.instituicao || "",
      agente_contratacao:
        edital.pregoeiro_nome ?? edital.pregoeiroNome ?? "",
      pregao_numero: edital.numero_pregao ?? edital.numeroPregao ?? "",
      processo_numero: edital.numero_processo ?? edital.numeroProcesso ?? "",
      sistema: edital.sistema_eletronico ?? edital.sistemaEletronico ?? "",
      identificacao: edital.identificacao || "",
      objeto: edital.objeto || "",
      prazo_validade: edital.validade_proposta ?? edital.validadeProposta ?? "",
      prazo_entrega: edital.forma_execucao ?? edital.formaExecucao ?? "",
      prazo_vigencia: edital.vigencia_contrato ?? edital.vigenciaContrato ?? "",
      prazo_pagamento: edital.forma_pagamento ?? edital.formaPagamento ?? "",
      itens,
      colunas_itens,
    }));
  };

  const handleChange = (field, value) => {
    setProposta(prev => ({ ...prev, [field]: value }));
  };

  const adicionarPrazoExtra = () => {
    setProposta((prev) => ({
      ...prev,
      prazos_extras: [
        ...(prev.prazos_extras || []),
        { id: crypto.randomUUID(), titulo: "", conteudo: "" },
      ],
    }));
  };

  const atualizarPrazoExtra = (index, patch) => {
    setProposta((prev) => {
      const list = [...(prev.prazos_extras || [])];
      if (!list[index]) return prev;
      list[index] = { ...list[index], ...patch };
      return { ...prev, prazos_extras: list };
    });
  };

  const removerPrazoExtra = (index) => {
    setProposta((prev) => ({
      ...prev,
      prazos_extras: (prev.prazos_extras || []).filter((_, i) => i !== index),
    }));
  };

  const handleSave = (finalizar = false) => {
    if (!semPregao && !proposta.edital_id) {
      toast.error('Selecione um pregão ou marque "Não associar a um pregão".');
      return;
    }

    const dadosParaSalvar = {
      edital_id: semPregao ? null : (proposta.edital_id || null),
      numero_proposta: proposta.numero_proposta || "",
      data_proposta: proposta.data_proposta,
      local_data_proposta: proposta.local_data_proposta || null,
      texto_valor_total: proposta.texto_valor_total || null,
      status: finalizar ? "finalizada" : "rascunho",
      ao_instituicao: proposta.ao_instituicao || "",
      agente_contratacao: proposta.agente_contratacao || "",
      pregao_numero: proposta.pregao_numero || "",
      processo_numero: proposta.processo_numero || "",
      sistema: proposta.sistema || "",
      identificacao: proposta.identificacao || "",
      objeto: proposta.objeto || "",
      itens: proposta.itens || [],
      colunas_itens: proposta.colunas_itens || null,
      campos_extras: proposta.campos_extras || [],
      valor_total_proposta: proposta.valor_total_proposta || 0,
      prazo_validade: proposta.prazo_validade || "",
      prazo_entrega: proposta.prazo_entrega || "",
      prazo_vigencia: proposta.prazo_vigencia || "",
      prazo_pagamento: proposta.prazo_pagamento || "",
      prazos_extras: proposta.prazos_extras || [],
      observacoes: proposta.observacoes || ""
    };
    
    saveMutation.mutate(dadosParaSalvar);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <Alert className="border-amber-200 bg-amber-50">
            <AlertDescription className="text-amber-900">
              Você precisa configurar os dados da empresa antes de criar propostas.
              <Button
                onClick={() => navigate(createPageUrl("ConfiguracaoEmpresa"))}
                className="ml-4"
                size="sm"
              >
                Configurar Agora
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  /** Em nova proposta, demais campos só após escolher pregão ou «sem pregão». */
  const podePreencherDemais = Boolean(propostaId) || semPregao || Boolean(proposta.edital_id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(createPageUrl("Propostas"))}
            className="rounded-xl"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              {propostaId ? 'Editar Proposta' : 'Nova Proposta de Preço'}
            </h1>
            <p className="text-slate-600 mt-1">Preencha os dados da proposta comercial</p>
          </div>
        </div>

        <div className="grid gap-6">
          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Informações Básicas
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Selecionar pregão{!semPregao ? " *" : ""}</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 rounded-lg border border-input bg-muted/40 px-3 py-2 text-sm text-slate-700 min-h-[40px] flex items-center">
                      {semPregao
                        ? "Proposta sem vínculo com pregão no sistema — preencha instituição, objeto e demais campos manualmente."
                        : proposta.edital_id
                          ? (() => {
                              const edital = (Array.isArray(editais) ? editais : []).find(e => e.id === proposta.edital_id);
                              if (!edital) return `Pregão selecionado (ID: ${proposta.edital_id.slice(0, 8)}…)`;
                              const inst = edital.instituicao || edital.titulo_licitacao || edital.nome_arquivo || "";
                              const pe = edital.numero_pregao ? ` • ${edital.numero_pregao}` : "";
                              const proc = edital.numero_processo ? ` • ${edital.numero_processo}` : "";
                              return inst ? `${inst}${pe}${proc}` : `Pregão (ID: ${edital.id.slice(0, 8)}…)`;
                            })()
                          : 'Nenhum pregão selecionado — clique em "Consultar pregão"'}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setModalPregaoAberto(true)}
                      className="shrink-0 gap-2"
                      disabled={semPregao}
                    >
                      <Search className="w-4 h-4" />
                      Consultar pregão
                    </Button>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-3">
                    <Checkbox
                      id="proposta-sem-pregao"
                      checked={semPregao}
                      onCheckedChange={(v) => {
                        const checked = v === true;
                        setSemPregao(checked);
                        if (checked) {
                          setProposta((prev) => ({ ...prev, edital_id: "" }));
                          setModalPregaoAberto(false);
                        }
                      }}
                      className="mt-0.5"
                    />
                    <div className="space-y-0.5 min-w-0">
                      <Label htmlFor="proposta-sem-pregao" className="font-medium cursor-pointer leading-snug">
                        Não associar a um pregão
                      </Label>
                      <p className="text-sm text-slate-600">
                        Use para propostas avulsas: os dados não serão importados de um edital. Desmarque para vincular a um pregão concluído na plataforma.
                      </p>
                    </div>
                  </div>
                  <SelecionarPregaoModal
                    open={modalPregaoAberto}
                    onOpenChange={setModalPregaoAberto}
                    editais={editais}
                    editalSelecionadoId={proposta.edital_id || null}
                    onSelecionar={(edital) => handleEditalChange(edital.id)}
                    statusFiltro="todos"
                    priorizarExtracaoConcluida
                    textoAjuda={`Todos os pregões da empresa são listados; os com extração concluída aparecem primeiro (etiqueta "Extração OK"). Use a busca para filtrar.`}
                    carregandoLista={
                      isPendingEditais ||
                      (isFetchingEditais && (!Array.isArray(editais) || editais.length === 0))
                    }
                    erroLista={
                      errorEditais
                        ? getDisplayMessage(
                            errorEditais,
                            "Não foi possível carregar os pregões. Verifique a ligação e tente novamente."
                          )
                        : null
                    }
                  />
                </div>

                {!propostaId && !podePreencherDemais ? (
                  <p className="text-sm text-slate-600 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 md:col-span-2">
                    Selecione um pregão ou marque &quot;Não associar a um pregão&quot; para preencher os demais campos.
                  </p>
                ) : null}

                <fieldset
                  disabled={!podePreencherDemais}
                  className="min-w-0 border-0 p-0 m-0 md:col-span-2 grid md:grid-cols-2 gap-4 disabled:opacity-60"
                >
                  <div className="space-y-2">
                    <Label>Número da Proposta</Label>
                    <Input
                      value={proposta.numero_proposta || ""}
                      onChange={(e) => handleChange('numero_proposta', e.target.value)}
                      placeholder="Ex: PROP-2025-001"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Data da Proposta</Label>
                    <Input
                      type="date"
                      value={proposta.data_proposta || ""}
                      onChange={(e) => handleChange('data_proposta', e.target.value)}
                    />
                  </div>
                </fieldset>
              </div>
            </CardContent>
          </Card>

          <fieldset
            disabled={!podePreencherDemais}
            className="min-w-0 border-0 p-0 m-0 grid gap-6 disabled:opacity-60"
          >
          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="border-b border-slate-100">
              <CardTitle>Dados do Destinatário</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Instituição (destinatário)</Label>
                  <Input
                    value={proposta.ao_instituicao || ""}
                    onChange={(e) => handleChange('ao_instituicao', e.target.value)}
                    placeholder="Ex.: Secretaria da Saúde (SESA) ou Ministério da Fazenda"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Agente de Contratação</Label>
                  <Input
                    value={proposta.agente_contratacao || ""}
                    onChange={(e) => handleChange('agente_contratacao', e.target.value)}
                    placeholder="Nome do Pregoeiro"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Pregão Eletrônico Nº</Label>
                  <Input
                    value={proposta.pregao_numero || ""}
                    onChange={(e) => handleChange('pregao_numero', e.target.value)}
                    placeholder="Ex: PE 90032/2025"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Processo</Label>
                  <Input
                    value={proposta.processo_numero || ""}
                    onChange={(e) => handleChange('processo_numero', e.target.value)}
                    placeholder="Número do Processo"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sistema</Label>
                  <Input
                    value={proposta.sistema || ""}
                    onChange={(e) => handleChange('sistema', e.target.value)}
                    placeholder="Ex: COMPRASNET"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Identificação</Label>
                  <Input
                    value={proposta.identificacao || ""}
                    onChange={(e) => handleChange('identificacao', e.target.value)}
                    placeholder="UASG ou outro identificador"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Objeto</Label>
                  <Textarea
                    value={proposta.objeto || ""}
                    onChange={(e) => handleChange('objeto', e.target.value)}
                    placeholder="Descrição completa do objeto da licitação"
                    rows={4}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <ItemsProposta
            key={proposta.edital_id || "sem-edital"}
            proposta={proposta}
            onChange={setProposta}
            onSaveImmediate={handleSaveImmediate}
            onImportacaoPlanilhaConcluida={() => setItensImportadosPlanilha(true)}
          />

          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="border-b border-slate-100">
              <CardTitle>Prazos e Condições</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prazo de Validade da Proposta</Label>
                  <Input
                    value={proposta.prazo_validade || ""}
                    onChange={(e) => handleChange('prazo_validade', e.target.value)}
                    placeholder="Ex: 60 (SESSENTA) DIAS"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Prazo de Entrega</Label>
                  <Input
                    value={proposta.prazo_entrega || ""}
                    onChange={(e) => handleChange('prazo_entrega', e.target.value)}
                    placeholder="CONFORME EDITAL E SEUS ANEXOS"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Prazo de Vigência do Contrato</Label>
                  <Input
                    value={proposta.prazo_vigencia || ""}
                    onChange={(e) => handleChange('prazo_vigencia', e.target.value)}
                    placeholder="Ex: 24 MESES CONFORME EDITAL"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Prazo de Pagamento</Label>
                  <Textarea
                    value={proposta.prazo_pagamento || ""}
                    onChange={(e) => handleChange('prazo_pagamento', e.target.value)}
                    placeholder="CONFORME EDITAL E SEUS ANEXOS"
                    rows={3}
                  />
                </div>

                <div className="space-y-3 md:col-span-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label className="text-base">Campos adicionais (prazos e condições)</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={adicionarPrazoExtra}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Adicionar campo
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500">
                    Cada campo aparece na proposta de preços e no PDF, com título e texto livre.
                  </p>
                  {(proposta.prazos_extras || []).length === 0 ? (
                    <p className="text-sm text-slate-400 italic">Nenhum campo extra. Use &quot;Adicionar campo&quot; se precisar.</p>
                  ) : (
                    <div className="space-y-3">
                      {(proposta.prazos_extras || []).map((row, index) => (
                        <div
                          key={row.id}
                          className="rounded-lg border border-slate-200 bg-slate-50/80 p-4 space-y-3"
                        >
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => removerPrazoExtra(index)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Remover
                            </Button>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wide text-slate-600">Título</Label>
                            <Input
                              value={row.titulo}
                              onChange={(e) =>
                                atualizarPrazoExtra(index, { titulo: e.target.value })
                              }
                              placeholder="Ex.: Garantia contratual, SLA de atendimento…"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wide text-slate-600">Conteúdo</Label>
                            <Textarea
                              value={row.conteudo}
                              onChange={(e) =>
                                atualizarPrazoExtra(index, { conteudo: e.target.value })
                              }
                              placeholder="Texto que constará na proposta"
                              rows={3}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Observações</Label>
                  <Textarea
                    value={proposta.observacoes || ""}
                    onChange={(e) => handleChange('observacoes', e.target.value)}
                    placeholder="Observações adicionais sobre a proposta"
                    rows={4}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          </fieldset>
        </div>

        <div className="flex justify-end gap-3 mt-6 mb-8">
          <Button
            variant="outline"
            onClick={() => navigate(createPageUrl("Propostas"))}
            disabled={saveMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saveMutation.isPending || !podePreencherDemais}
          >
            <Save className="w-4 h-4 mr-2" />
            Salvar Rascunho
          </Button>
          <Button
            onClick={() => handleSave(true)}
            disabled={saveMutation.isPending || !podePreencherDemais}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Send className="w-4 h-4 mr-2" />
            Finalizar Proposta
          </Button>
        </div>
      </div>
    </div>
  );
}